<template>
    <div
        v-if="visible"
        class="telegram-install"
        :class="{'telegram-install--success': trigger === 'success'}"
    >
        <div class="telegram-install__inner">
            <button
                type="button"
                class="telegram-install__close"
                :aria-label="waitForChoice ? 'Готово, закрыть' : 'Скрыть предложение'"
                :disabled="choiceInProgress"
                @click="finish(waitForChoice ? 'done' : 'session-dismissed')"
            >
                <i class="fas fa-times" aria-hidden="true"></i>
            </button>
            <InstallPromotionCard
                :success="trigger === 'success'"
                :show-done="waitForChoice"
                :preview-size="trigger === 'success' ? 'mini' : 'compact'"
                :busy="choiceInProgress"
                @install="install"
                @installed="installed"
                @snooze="snooze"
                @done="finish('done')"
            />
            <p
                v-if="installError"
                class="telegram-install__error"
                role="alert"
            >
                {{ installError }}
            </p>
        </div>
    </div>
</template>

<script>
import InstallPromotionCard from '@/components/InstallPromotionCard.vue';
import {
    INSTALLATION_STATE_CHANGED_EVENT,
    isIOSInstallDevice,
    markAppAlreadyInstalled,
    markInstallPromotionSessionDismissed,
    markTelegramInstallSuccessEligible,
    openInstallUrlExternally,
    prepareTelegramInstallHandoff,
    shouldShowInstallPromotion,
    snoozeInstallPromotion
} from '@/utils/appInstallation';
import {registerInstallPromotionPresenter} from '@/utils/installPromotion';

const TELEGRAM_INSTALL_OPEN_SHOWN_SESSION_KEY =
    'argus.install.telegramOpenShown.v1';
const TELEGRAM_INSTALL_SUCCESS_SHOWN_SESSION_KEY =
    'argus.install.telegramSuccessShown.v1';

function readSessionFlag(key) {
    try {
        return window.sessionStorage.getItem(key) === '1';
    } catch (error) {
        return false;
    }
}

function writeSessionFlag(key) {
    try {
        window.sessionStorage.setItem(key, '1');
    } catch (error) {
        // The in-memory flag still limits the current component instance.
    }
}

export default {
    name: 'TelegramInstallPromotion',
    components: {
        InstallPromotionCard
    },
    data() {
        return {
            visible: false,
            trigger: 'open',
            waitForChoice: false,
            openShown: readSessionFlag(TELEGRAM_INSTALL_OPEN_SHOWN_SESSION_KEY),
            successShown: readSessionFlag(
                TELEGRAM_INSTALL_SUCCESS_SHOWN_SESSION_KEY
            ),
            unregisterPresenter: null,
            pendingResolver: null,
            eligibilityCheckId: 0,
            choiceInProgress: false,
            installError: ''
        };
    },
    computed: {
        navigationLoaded() {
            const navigation = this.$store.state.navigationExperience || {};
            return navigation.loaded === true;
        },
        usesModernUi() {
            return this.navigationLoaded
                && this.$store.getters.navigationReady;
        },
        isTelegram() {
            return this.$store.getters.isTelegramWebApps;
        },
        canCampaignRun() {
            return this.isTelegram
                && this.$store.getters.isAuthorized
                && this.usesModernUi
                && this.$route.path !== '/telegram-datepicker';
        }
    },
    watch: {
        canCampaignRun: {
            immediate: true,
            handler(value) {
                if (value) {
                    this.tryShowOnOpen();
                } else {
                    this.finish('unavailable');
                }
            }
        }
    },
    mounted() {
        this.unregisterPresenter = registerInstallPromotionPresenter(
            this.presentPromotion
        );
        window.addEventListener(
            INSTALLATION_STATE_CHANGED_EVENT,
            this.onInstallationStateChanged
        );
    },
    beforeDestroy() {
        if (this.unregisterPresenter) {
            this.unregisterPresenter();
            this.unregisterPresenter = null;
        }
        window.removeEventListener(
            INSTALLATION_STATE_CHANGED_EVENT,
            this.onInstallationStateChanged
        );
        this.finish('destroyed');
    },
    methods: {
        async tryShowOnOpen() {
            if (!this.canCampaignRun || this.openShown || isIOSInstallDevice()) {
                return;
            }
            await this.presentPromotion({
                trigger: 'open',
                waitForChoice: false,
                context: {source: 'telegram_open'}
            });
        },
        async presentPromotion(request = {}) {
            if (!this.canCampaignRun) return {shown: false};
            const trigger = request.trigger === 'success' ? 'success' : 'open';
            if (trigger === 'success') {
                markTelegramInstallSuccessEligible();
            }
            if (trigger === 'open' && (this.openShown || isIOSInstallDevice())) {
                return {shown: false};
            }
            if (trigger === 'success' && this.successShown) {
                return {shown: false};
            }

            const checkId = ++this.eligibilityCheckId;
            const eligible = await shouldShowInstallPromotion();
            if (
                checkId !== this.eligibilityCheckId
                || !eligible
                || !this.canCampaignRun
            ) {
                return {shown: false};
            }

            const handoff = await prepareTelegramInstallHandoff();
            if (
                checkId !== this.eligibilityCheckId
                || !handoff
                || !this.canCampaignRun
            ) {
                return {shown: false};
            }

            if (trigger === 'open') {
                this.openShown = true;
                writeSessionFlag(TELEGRAM_INSTALL_OPEN_SHOWN_SESSION_KEY);
            }
            if (trigger === 'success') {
                this.successShown = true;
                writeSessionFlag(TELEGRAM_INSTALL_SUCCESS_SHOWN_SESSION_KEY);
            }
            this.trigger = trigger;
            this.waitForChoice = request.waitForChoice === true;
            this.installError = '';
            this.visible = true;

            if (!this.waitForChoice) {
                return {shown: true};
            }
            if (this.pendingResolver) {
                this.pendingResolver({shown: true, action: 'replaced'});
            }
            return new Promise(resolve => {
                this.pendingResolver = resolve;
            });
        },
        async install() {
            if (this.choiceInProgress) return;
            this.choiceInProgress = true;
            this.installError = '';
            try {
                const opened = await openInstallUrlExternally();
                this.choiceInProgress = false;
                if (opened) {
                    this.finish('install');
                } else {
                    this.installError = 'Не удалось подготовить безопасный вход. Проверьте интернет и повторите.';
                }
            } finally {
                this.choiceInProgress = false;
            }
        },
        async installed() {
            if (this.choiceInProgress) return;
            this.choiceInProgress = true;
            try {
                await markAppAlreadyInstalled();
            } finally {
                this.choiceInProgress = false;
                this.finish('installed');
            }
        },
        async snooze() {
            if (this.choiceInProgress) return;
            this.choiceInProgress = true;
            try {
                await snoozeInstallPromotion();
            } finally {
                this.choiceInProgress = false;
                this.finish('snoozed');
            }
        },
        finish(action) {
            if (
                this.choiceInProgress
                && action !== 'destroyed'
                && action !== 'unavailable'
            ) {
                return;
            }
            if (action === 'session-dismissed') {
                markInstallPromotionSessionDismissed();
            }
            const wasVisible = this.visible;
            this.visible = false;
            this.waitForChoice = false;
            this.installError = '';
            if (this.pendingResolver) {
                const resolve = this.pendingResolver;
                this.pendingResolver = null;
                resolve({shown: wasVisible, action});
            }
        },
        async onInstallationStateChanged() {
            if (!this.visible || this.choiceInProgress) return;
            const eligible = await shouldShowInstallPromotion();
            if (!eligible && !this.choiceInProgress) {
                this.finish('state-changed');
            }
        }
    }
};
</script>

<style scoped>
.telegram-install {
    position: relative;
    z-index: 1080;
    width: 100%;
    padding: 8px 10px 0;
    background: #eef4fb;
}

.telegram-install--success {
    position: fixed;
    z-index: 1400;
    right: 0;
    bottom: 0;
    left: 0;
    max-height: 100vh;
    max-height: 100dvh;
    padding: 10px;
    overflow-y: auto;
    background: linear-gradient(to top, rgba(12, 28, 49, .24), transparent);
    pointer-events: none;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
}

.telegram-install__inner {
    position: relative;
    width: min(980px, 100%);
    margin: 0 auto;
    pointer-events: auto;
}

.telegram-install__close {
    position: absolute;
    z-index: 2;
    top: 8px;
    right: 8px;
    display: inline-flex;
    width: 32px;
    height: 32px;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 10px;
    color: #e6f2ff;
    background: rgba(255, 255, 255, .12);
    cursor: pointer;
}

.telegram-install__close:focus-visible {
    outline: 3px solid rgba(255, 255, 255, .54);
    outline-offset: 2px;
}

.telegram-install__close:disabled {
    cursor: wait;
    opacity: .55;
}

.telegram-install__error {
    position: relative;
    z-index: 3;
    margin: 8px 40px 0;
    padding: 9px 12px;
    border: 1px solid rgba(255, 211, 106, .55);
    border-radius: 10px;
    color: #fff4d4;
    background: rgba(86, 57, 0, .88);
    font-size: .875rem;
    line-height: 1.35;
    text-align: center;
}

@media (max-width: 575.98px) {
    .telegram-install,
    .telegram-install--success {
        padding: 7px 7px 0;
    }

    .telegram-install--success {
        padding-bottom: calc(7px + env(safe-area-inset-bottom));
    }

    .telegram-install__error {
        margin-right: 34px;
        margin-left: 34px;
    }
}
</style>
